import api from '../../utils/api';
import {
  BookingResponse,
  AmbulanceBooking,
  AmbulancePricingSlab,
  AmbulanceVendorServiceArea,
  VendorDocument,
} from '../../models/MasterAdmin/ambulanceBookings';

export interface BookingsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export const fetchAmbulanceProfile = async (vendorId: string) => {
  try {
    const response = await api.get(`/master-admin/ambulance/${vendorId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ambulance profile:', error);
    throw error;
  }
};

export const fetchAmbulanceBookings = async (vendorId: string, params?: BookingsParams) => {
  try {
    const qs = new URLSearchParams();
    if (params?.page != null) qs.set('page', String(params.page));
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    if (params?.status && params.status !== 'all') qs.set('status', params.status);
    const url = `/master-admin/bookings/${vendorId}${qs.toString() ? `?${qs.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data as BookingResponse & { pagination?: { total: number; page: number; limit: number; totalPages: number } };
  } catch (error) {
    console.error('Error fetching ambulance bookings:', error);
    throw error;
  }
};

export type AmbulanceBookingInviteRow = {
  inviteId: string;
  vendorId: string;
  status: string;
  mode: string;
  agencyName: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Full booking row from master-admin detail API (matches AmbulanceBooking model). */
export type AmbulanceBookingDetail = {
  bookingId: string;
  userId: string;
  vendorId: string | null;
  dispatchMode: string;
  staggeredBroadcastVendorIds: string[] | null;
  staggeredBroadcastIssuedCount: number | null;
  bookingFor: string;
  patientName: string;
  patientPhone: string;
  patientAge: number | null;
  patientGender: string | null;
  pickupAddress: string;
  pickupLocation: { lat: number; lng: number } | string;
  pickupLandmark: string | null;
  dropAddress: string;
  dropLocation: { lat: number; lng: number } | string;
  dropLandmark: string | null;
  pickupTimeType: string;
  scheduledPickupAt: string | null;
  ambulanceType: string;
  facilities: string[] | null;
  isDeadBody: boolean;
  isReturnJourney: boolean;
  isEmergency: boolean;
  totalDistanceKm: number;
  estimatedTravelTimeMinutes: number;
  baseFare: number;
  subTotal: number;
  totalPayableAmount: number;
  paymentId: string | null;
  paymentMethod: string;
  paymentStatus: string;
  paymentCompletedAt: string | null;
  callInitiatedAt: string | null;
  supportIssueNote: string | null;
  acceptedAt: string | null;
  bookingStatus: string;
  bookingStatusLabel?: string;
  status?: string;
  cancelledBy: string | null;
  cancellationReason: string | null;
  cancelledAt: string | null;
  refundAmount: number | null;
  refundStatus: string;
  ambulanceLeftAt: string | null;
  expectedCompletionAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  pickupCoordinates?: { lat: number; lng: number } | null;
  dropCoordinates?: { lat: number; lng: number } | null;
  assignedAgencyName?: string | null;
  invites?: AmbulanceBookingInviteRow[];
  user?: { userId: string; name?: string; phone_number?: string; emailId?: string };
};

export const fetchAmbulanceBookingById = async (
  vendorId: string,
  bookingId: string
): Promise<AmbulanceBookingDetail> => {
  try {
    const response = await api.get(`/master-admin/bookings/${vendorId}/booking/${bookingId}`);
    return response.data as AmbulanceBookingDetail;
  } catch (error) {
    console.error('Error fetching ambulance booking:', error);
    throw error;
  }
};

export interface LocationAnalyticsResponse {
  topPickupLocations: { location: string; count: number }[];
  topDropLocations: { location: string; count: number }[];
}

export const fetchLocationAnalytics = async (vendorId: string) => {
  try {
    const response = await api.get(`/master-admin/ambulance/${vendorId}/location-analytics`);
    return response.data as LocationAnalyticsResponse;
  } catch (error) {
    console.error('Error fetching location analytics:', error);
    throw error;
  }
};

export interface VehicleUsageResponse {
  byType: { type: string; count: number; percentage: number }[];
  total: number;
}

export const fetchVehicleUsage = async (vendorId: string) => {
  try {
    const response = await api.get(`/master-admin/ambulance/${vendorId}/vehicle-usage`);
    return response.data as VehicleUsageResponse;
  } catch (error) {
    console.error('Error fetching vehicle usage:', error);
    throw error;
  }
};

export interface VendorFinancialOverview {
  pending?: { amount: number; count: number };
  on_hold?: { amount: number; count: number };
  settled?: { amount: number; count: number };
  total?: { amount: number; count: number };
  totalPaidOut?: number;
  totalRefundAmount?: number;
  earningsByStatus?: Record<string, { amount: number; count: number }>;
}

export const fetchVendorFinancialOverview = async (vendorId: string, period: string = 'all') => {
  try {
    const response = await api.get(`/settlement/vendor/${vendorId}/dashboard`, { params: { period } });
    return response.data?.data as VendorFinancialOverview;
  } catch (error) {
    console.error('Error fetching vendor financial overview:', error);
    throw error;
  }
}; 

export const fetchAmbulanceInvoiceBlob = async (bookingId: string): Promise<Blob> => {
  const encodedBookingId = encodeURIComponent(bookingId);
  const response = await api.get(`/ambulance-invoice/ambulance/invoice/${encodedBookingId}/master-admin`, {
    responseType: 'blob',
  });
  return response.data as Blob;
};

export interface UpsertAmbulancePricingPayload {
  vehicleType: string;
  fromKm: number;
  toKm: number | null;
  pricingMode: 'FLAT' | 'PER_KM';
  WITH_OXYGEN?: number;
  WITHOUT_OXYGEN?: number;
  AC_WITH_OXYGEN?: number;
  AC_WITHOUT_OXYGEN?: number;
  attendantCharge?: number;
  mortuaryCharge?: number;
  facilityCharges?: Record<string, number> | null;
  isActive?: boolean;
}

export const fetchAmbulancePricing = async (vendorId: string) => {
  const response = await api.get(`/master-admin/ambulance/${vendorId}/pricing`);
  return response.data as { vendorId: string; count: number; pricing: AmbulancePricingSlab[] };
};

export const createAmbulancePricing = async (
  vendorId: string,
  payload: UpsertAmbulancePricingPayload
) => {
  const response = await api.post(`/master-admin/ambulance/${vendorId}/pricing`, payload);
  return response.data as { message: string; pricing: AmbulancePricingSlab };
};

export const updateAmbulancePricing = async (
  vendorId: string,
  pricingId: string,
  payload: UpsertAmbulancePricingPayload
) => {
  const response = await api.put(`/master-admin/ambulance/${vendorId}/pricing/${pricingId}`, payload);
  return response.data as { message: string; pricing: AmbulancePricingSlab };
};

export const deleteAmbulancePricing = async (vendorId: string, pricingId: string) => {
  const response = await api.delete(`/master-admin/ambulance/${vendorId}/pricing/${pricingId}`);
  return response.data as { message: string };
};

export type UploadAmbulanceVendorDocumentPayload = {
  documentName: string;
  documentNumber?: string;
  expiryDate?: string;
  file: File;
};

export const uploadAmbulanceVendorDocument = async (
  vendorId: string,
  payload: UploadAmbulanceVendorDocumentPayload
): Promise<VendorDocument | null> => {
  const formData = new FormData();
  formData.append('documentName', payload.documentName);
  if (payload.documentNumber) {
    formData.append('documentNumber', payload.documentNumber);
  }
  if (payload.expiryDate) {
    formData.append('expiryDate', payload.expiryDate);
  }
  formData.append('documentFile', payload.file);

  const response = await api.post<{
    success?: boolean;
    document?: VendorDocument;
    message?: string;
  }>(`/master-admin/ambulance/${vendorId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data?.document ?? null;
};

export const uploadAmbulancePricingExcel = async (
  vendorId: string,
  file: File,
  replaceExisting: boolean = true
) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `/master-admin/ambulance/${vendorId}/pricing/upload?replaceExisting=${replaceExisting}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data as { message: string; insertedCount: number; replaceExisting: boolean };
};

export interface UpsertAmbulanceServiceAreaPayload {
  pinCode?: string;
  areaName?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radiusKm?: number | null;
  isActive?: boolean;
}

export const fetchAmbulanceServiceAreas = async (vendorId: string) => {
  const response = await api.get(`/master-admin/ambulance/${vendorId}/service-areas`);
  return response.data as { serviceAreas: AmbulanceVendorServiceArea[] };
};

export const createAmbulanceServiceArea = async (
  vendorId: string,
  payload: UpsertAmbulanceServiceAreaPayload
) => {
  const response = await api.post(`/master-admin/ambulance/${vendorId}/service-areas`, payload);
  return response.data as { message: string; serviceArea: AmbulanceVendorServiceArea };
};

export const updateAmbulanceServiceArea = async (
  vendorId: string,
  serviceAreaId: string,
  payload: UpsertAmbulanceServiceAreaPayload
) => {
  const response = await api.patch(
    `/master-admin/ambulance/${vendorId}/service-areas/${serviceAreaId}`,
    payload
  );
  return response.data as { message: string; serviceArea: AmbulanceVendorServiceArea };
};

export const deleteAmbulanceServiceArea = async (vendorId: string, serviceAreaId: string) => {
  const response = await api.delete(`/master-admin/ambulance/${vendorId}/service-areas/${serviceAreaId}`);
  return response.data as { message: string };
};

export type AmbulanceFleetBlockedBy = 'master_admin' | 'ambulance_provider' | null;

export type AmbulanceFleetDriver = {
  driverId: string;
  vendorId: string;
  name: string;
  phoneNumber: string;
  isActive: boolean;
  isBlocked: boolean;
  blockedBy: AmbulanceFleetBlockedBy;
  blockedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AmbulanceFleetVehicle = {
  vehicleId: string;
  vendorId: string;
  numberPlate: string;
  isActive: boolean;
  isBlocked: boolean;
  blockedBy: AmbulanceFleetBlockedBy;
  blockedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export const fetchAmbulanceFleetDrivers = async (vendorId: string) => {
  const response = await api.get(`/master-admin/ambulance/${vendorId}/drivers`);
  return response.data as { success: boolean; data: AmbulanceFleetDriver[] };
};

export const createAmbulanceFleetDriver = async (
  vendorId: string,
  payload: { name: string; phoneNumber: string }
) => {
  const response = await api.post(`/master-admin/ambulance/${vendorId}/drivers`, payload);
  return response.data as { success: boolean; data: AmbulanceFleetDriver; message?: string };
};

export const blockAmbulanceFleetDriver = async (vendorId: string, driverId: string) => {
  const response = await api.patch(`/master-admin/ambulance/${vendorId}/drivers/${driverId}/block`);
  return response.data as { success: boolean; data: AmbulanceFleetDriver; message?: string };
};

export const unblockAmbulanceFleetDriver = async (vendorId: string, driverId: string) => {
  const response = await api.patch(`/master-admin/ambulance/${vendorId}/drivers/${driverId}/unblock`);
  return response.data as { success: boolean; data: AmbulanceFleetDriver; message?: string };
};

export const fetchAmbulanceFleetVehicles = async (vendorId: string) => {
  const response = await api.get(`/master-admin/ambulance/${vendorId}/vehicles`);
  return response.data as { success: boolean; data: AmbulanceFleetVehicle[] };
};

export const createAmbulanceFleetVehicle = async (
  vendorId: string,
  payload: { numberPlate: string }
) => {
  const response = await api.post(`/master-admin/ambulance/${vendorId}/vehicles`, payload);
  return response.data as { success: boolean; data: AmbulanceFleetVehicle; message?: string };
};

export const blockAmbulanceFleetVehicle = async (vendorId: string, vehicleId: string) => {
  const response = await api.patch(`/master-admin/ambulance/${vendorId}/vehicles/${vehicleId}/block`);
  return response.data as { success: boolean; data: AmbulanceFleetVehicle; message?: string };
};

export const unblockAmbulanceFleetVehicle = async (vendorId: string, vehicleId: string) => {
  const response = await api.patch(`/master-admin/ambulance/${vendorId}/vehicles/${vehicleId}/unblock`);
  return response.data as { success: boolean; data: AmbulanceFleetVehicle; message?: string };
};